import React from 'react'
import styled from 'styled-components'
import { COMPETITORS, TABLE_COLUMNS } from './competitors'
import { FREE_STRETCH_COUNT, STRETCH_COUNT } from '../lib/product'

const COUNTS = { stretchCount: STRETCH_COUNT, freeStretchCount: FREE_STRETCH_COUNT }

// Localised pages pass `t` and the locale files are authoritative. The English
// /blog/ comparison pages pass no `t` and read competitors.js directly.
const localise = (t, key, english) => (t ? t(key, COUNTS) : english)

// Wider than the text column it sits in, so all eight columns fit on a laptop.
const Scroller = styled.div`
    overflow-x: auto;
    margin: 24px 0 8px;

    /* Sits just past the text column rather than dominating the page. Below
       this it scrolls, which 7 columns of prose need somewhere. */
    @media (min-width: 1020px) {
        width: 960px;
        margin-left: calc(50% - 480px);
    }

    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 14px;
    -webkit-overflow-scrolling: touch;
`

const Table = styled.table`
    width: 100%;
    min-width: 820px;
    border-collapse: collapse;
    margin: 0;
    font-size: 0.82rem;
    line-height: 1.45;

    th,
    td {
        padding: 9px 12px;
        text-align: left;
        vertical-align: top;
        border-bottom: 1px solid #f1f5f9;
        color: #334155;
    }

    thead th {
        background: #f8fafc;
        color: #0f172a;
        font-weight: 700;
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.3px;
    }

    tbody th {
        font-weight: 700;
        color: #0f172a;
        white-space: nowrap;
    }

    tbody tr:last-child th,
    tbody tr:last-child td {
        border-bottom: none;
    }

    tr.own {
        background: rgba(32, 159, 255, 0.04);
    }
`

const hostOf = (href) => {
    const [, host] = /^https?:\/\/([^/]+)/.exec(href) || []
    return host ? host.replace(/^www\./, '') : 'source'
}

const Source = styled.a`
    display: inline-block;
    margin-left: 4px;
    font-size: 0.72rem;
    color: #94a3b8;
    text-decoration: underline;
`

const isOwn = (href) => href.includes('keybreak.co.uk')

// The leading space matters: without it the anchor butts against the cell text
// and anything extracting the page reads "Windowssource".
export const SourceLink = ({ href }) =>
    href ? (
        <>
            {' '}
            <Source
                href={href}
                aria-label={`Source: ${hostOf(href)}`}
                {...(isOwn(href) ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
            >
                source
            </Source>
        </>
    ) : null

// JSX renders nothing at all for true, false, null and undefined, so a column
// backed by a boolean field comes out blank for every app. null means "not
// verified" in competitors.js and must never be shown as a yes or a no.
const cell = (value, t, id, key) => {
    if (value === true) return localise(t, 'table.yes', 'Yes')
    if (value === false) return localise(t, 'table.no', 'No')
    if (value === null || value === undefined) return localise(t, 'table.notVerified', 'Not verified')
    if (typeof value === 'number') return String(value)
    return localise(t, `competitors.${id}.${key}`, value)
}

export default ({ apps, columns = TABLE_COLUMNS, t }) => (
    <Scroller>
        <Table>
            <thead>
                <tr>
                    <th scope="col">{localise(t, 'table.app', 'App')}</th>
                    {columns.map(({ key, label }) => (
                        <th scope="col" key={key}>
                            {localise(t, `table.columns.${key}`, label)}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {apps.map((id) => {
                    const app = COMPETITORS[id]
                    return (
                        <tr key={id} className={id === 'keybreak' ? 'own' : undefined}>
                            <th scope="row">
                                <a
                                    href={app.url}
                                    {...(id === 'keybreak'
                                        ? {}
                                        : { target: '_blank', rel: 'noopener noreferrer' })}
                                >
                                    {app.name}
                                </a>
                            </th>
                            {columns.map(({ key }) => (
                                <td key={key}>
                                    {cell(app[key], t, id, key)}
                                    <SourceLink href={app.sources[key]} />
                                </td>
                            ))}
                        </tr>
                    )
                })}
            </tbody>
        </Table>
    </Scroller>
)
